import React, { useEffect, useMemo } from 'react';
import { debounce, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useJobTitlesSearch } from '@/data/taxonomy/job-titles-search';
import type { JobTitle } from '@/models/taxonomy/job';

interface JobSearchProps {
  jobTitle: JobTitle | null;
  onSelect: (jobTitle: JobTitle | null) => void;
}
const isValidJobTitle = (job: JobTitle | null): job is JobTitle =>
  job !== null && typeof job?.id === 'number' && typeof job?.name === 'string';

export const JobSearch = ({ jobTitle, onSelect }: JobSearchProps) => {
  const validJobTitle = isValidJobTitle(jobTitle) ? jobTitle : null;

  const [value, setValue] = React.useState<JobTitle | null>(validJobTitle);
  const [inputValue, setInputValue] = React.useState(validJobTitle?.name ?? '');
  const [term, setTerm] = React.useState('');
  const { options, isLoading } = useJobTitlesSearch(term);

  useEffect(() => {
    const valid = isValidJobTitle(jobTitle) ? jobTitle : null;
    setValue(valid);
    setInputValue(valid?.name ?? '');
  }, [jobTitle]);

  const debouncedSetTerm = useMemo(() => debounce(setTerm, 500), [setTerm]);

  return (
    <Autocomplete
      // filterOptions is here because we're disabling the component's filtering - rely on backend results
      filterOptions={(x) => x}
      options={options}
      value={value}
      renderInput={(params) => (
        <TextField {...params} placeholder="Selecione" />
      )}
      onOpen={() => {
        setTerm(validJobTitle?.name ?? '');
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        debouncedSetTerm(newInputValue);
      }}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      loadingText="Buscando cargos..."
      noOptionsText={
        options.length === 0
          ? 'Digite para buscar cargos'
          : 'Nenhum resultado no momento. Talvez o cargo esteja escrito de outra forma.'
      }
      onChange={(_, option: JobTitle | null) => {
        setValue(option);
        onSelect(option);
      }}
    />
  );
};
